import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel, CheckboxIcon } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { capitalize } from "@helpers/general";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { mutate } from "swr";
import { setGlobalDataIsUpdated } from "../store";
import { store } from "@components/VerticalLayout/store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

type ConfirmationModificationMessageType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    setIsEdited: Function;
    setDataIsUpdated: Function;
    dataPosEditor: any;
    associated: any[];
    isAddFiscalYearClicked: boolean;
    setIsAddFiscalYearClicked: Function;
};

export function ConfirmationModificationMessage({
    isModalOpened,
    setIsModalOpened,
    setIsEdited,
    setDataIsUpdated,
    dataPosEditor,
    associated,
    isAddFiscalYearClicked,
    setIsAddFiscalYearClicked,
}: ConfirmationModificationMessageType) {
    const { t } = useTranslation();

    const { userID, franchiseID, operatorID } = useSnapshot(store);

    const [error, setError] = React.useState<boolean>(false);
    const uuidUser = localStorage.getItem("uuidUser");

    async function editPosEditor() {
        toast.dismiss();
        let objectData = {};
        let localData: any[] = [];

        associated.forEach(async (el: any) => {
            Object.assign(objectData, {
                [el.Id]: {
                    posEditor: dataPosEditor.data[el.Id].posEditor,
                    note: dataPosEditor.data[el.Id].note,
                    codeComment: el["codeComment"],
                    isActive: el.isActive,
                    isArchived: el.isArchived,
                    shopId: el.shopId,
                    user: el.user,
                    versionServer: el.versionServer,
                    keyAuthentication: el.keyAuthentication,
                    apiEmail: el.apiEmail,
                    port: el.port,
                    server: el.server,
                    apiServer: el.apiServer,
                    setupId: el.setupId,
                    apiPassword: el.apiPassword,
                    modeOfSaleEmpId: el.modeOfSaleEmpId,
                    modeOfSaleLivId: el.modeOfSaleLivId,
                    modeOfSaleSPId: el.modeOfSaleSPId,
                    divisionPriceItems: el.divisionPriceItems,
                    divisionPriceOptions: el.divisionPriceOptions,
                    divisionTax: el.divisionTax,
                    layoutLabel: el.layoutLabel,
                    apiWebhook: el.apiWebhook,
                    happyHour:el?.happyHour,
                    happyHourState:el?.happyHourState,
                    tcposStoreId: el?.tcposStoreId,
                    tcposWebhookPort: el?.tcposWebhookPort,
                    tcposWebhookIPAddess: el?.tcposWebhookIPAddess,
                    tcposFranchiseSchema: el?.tcposFranchiseSchema,
                    tcposPaymentCash: el?.tcposPaymentCash,
                    tcposPaymentCreditCard: el?.tcposPaymentCreditCard,
                    tcposSetupDisk: el?.tcposSetupDisk,
                    tcposCodeCountry: el?.tcposCodeCountry,
                    tcposLangue: el?.tcposLangue,
                    tcposApiPort: el?.tcposApiPort,
                    tcposPartnerApiUrl: el?.tcposPartnerApiUrl,
                    tcposIp: el?.tcposIp,
                    tcposConnectionCheckTime: el?.tcposConnectionCheckTime,
                    tcposDeletingDataSate: el?.tcposDeletingDataSate,
                    codeRetryTable: el?.codeRetryTable,
                    maxRetry: el?.maxRetry,
                    retryDelai: el?.retryDelai,
                    checkStock: el?.checkStock,
                    orderValidationPreCheck: el?.orderValidationPreCheck,
                    timeoutAPI: el?.timeoutAPI,
                    automaticProductionLevel: el?.automaticProductionLevel,
                    tcposCorrTakeawayDisplayName:
                        el?.tcposCorrTakeawayDisplayName,
                    tcposCorrTakeawayID: el?.tcposCorrTakeawayID,
                    tcposCorrOnSiteDisplayName: el?.tcposCorrOnSiteDisplayName,
                    tcposCorrOnSiteID: el?.tcposCorrOnSiteID,
                },
            });

            let dataOfPosEditor = {
                userId: userID,
                shopId: [el.shopId],
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: objectData,
                isShop: true,
                isAssociate: true,
            };

            localData.push(
                fetch(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/posEditor`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(dataOfPosEditor),
                    }
                ).then((response) => response.json())
            );
        });
        try {
            mutate(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/posEditor`,
                await Promise.all(localData).then((result) => {
                    result.forEach((element: any) => {
                        if (element.error === true) setError(true);
                    });
                })
            );
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        if (error === false) {
            toast.success(
                `${t("The Pos Editor has been successfully modified")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        }
        setDataIsUpdated(true);
        setIsAddFiscalYearClicked(!isAddFiscalYearClicked);
        setIsEdited(false);
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                }}
            >
                <span
                    style={{
                        fontFamily: "Segoe UI Semibold",
                        color: "#2b2828",
                        fontSize: "20px",
                    }}
                >
                    {capitalize(t("Alert"))}
                </span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(false);
                        setIsAddFiscalYearClicked(false);
                        setIsEdited(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        editPosEditor();
                        setGlobalDataIsUpdated(true);
                        setIsAddFiscalYearClicked(false);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
